import {getIssueFieldBadgesFromValues, type IssueFieldBadge, type IssueFieldValue} from '@github-ui/issue-field-badges'
import {ListItemTrailingBadge} from '@github-ui/list-view/ListItemTrailingBadge'
import {ActionList, ActionMenu, Button, Tooltip} from '@primer/react'
import type {KeyboardEvent, ReactElement} from 'react'

import styles from './IssueFieldBadges.module.css'

// Inline cap for issue field pills before the remainder collapses into a
// `+N` overflow menu, matching the Relay row (`MAX_INLINE_FIELDS`). Labels are
// never capped; only the (newer) field pills collapse.
export const MAX_INLINE_FIELDS = 4

const MAX_TEXT_LENGTH = 30

function truncateValue(value: string): string {
  if (value.length <= MAX_TEXT_LENGTH) return value
  return `${value.slice(0, MAX_TEXT_LENGTH)}…`
}

// Stop the list-view keyboard navigation from swallowing keystrokes that should
// activate the pill (mirrors the Relay IssueFieldValueBadge behavior).
function stopKeyPropagation(event: KeyboardEvent<HTMLButtonElement>) {
  event.stopPropagation()
}

function IssueFieldValueToken({displayValue}: {displayValue: string}) {
  return (
    <span className={styles.token} data-testid="issue-field-value-badge">
      {truncateValue(displayValue)}
    </span>
  )
}

interface RenderIssueFieldBadgesOptions {
  fieldValues: IssueFieldValue[]
  /** Invoked with the field name + query value when a pill is clicked. When omitted the pills are static. */
  onSelect?: (fieldName: string, queryValue: string) => void
  /** Class applied to the filter button so each row can preserve its own link styling. */
  filterLinkClassName?: string
}

function InlineFieldBadge({
  badge,
  onSelect,
  filterLinkClassName,
}: {
  badge: IssueFieldBadge
  onSelect?: (fieldName: string, queryValue: string) => void
  filterLinkClassName?: string
}) {
  const fullText = `${badge.fieldName}: ${badge.displayValue}`

  if (!onSelect) {
    return (
      <ListItemTrailingBadge title={fullText}>
        <span className={styles.token} data-testid="issue-field-value-badge">
          <span aria-hidden="true">{truncateValue(badge.displayValue)}</span>
          <span className="sr-only">{fullText}</span>
        </span>
      </ListItemTrailingBadge>
    )
  }

  return (
    <ListItemTrailingBadge title={fullText}>
      {/* Tooltip supplies the accessible label (`type="label"`), so its text
          must include the action — "Filter by ..." — not just the field/value.
          Primer requires interactive button names to convey action + context. */}
      <Tooltip text={`Filter by ${fullText}`} type="label">
        <button
          type="button"
          className={filterLinkClassName ?? styles.link}
          onClick={() => onSelect(badge.fieldName, badge.queryValue)}
          onKeyDown={stopKeyPropagation}
          data-testid="issue-field-value-badge-link"
        >
          <IssueFieldValueToken displayValue={badge.displayValue} />
        </button>
      </Tooltip>
    </ListItemTrailingBadge>
  )
}

function IssueFieldOverflowMenu({
  hiddenFieldCount,
  fields,
  onSelect,
}: {
  hiddenFieldCount: number
  fields: IssueFieldBadge[]
  onSelect?: (fieldName: string, queryValue: string) => void
}) {
  if (hiddenFieldCount <= 0 || fields.length === 0) return null

  // The tooltip text is short for visual users; screen readers get a fully
  // qualified accessible name that includes the count and what the button
  // controls (Primer requires button names to convey action + context).
  const tooltipText = `Show ${hiddenFieldCount} more`
  const accessibleName = `Show ${hiddenFieldCount} more issue field ${hiddenFieldCount === 1 ? 'value' : 'values'}`

  return (
    <ListItemTrailingBadge title={accessibleName}>
      <ActionMenu>
        <ActionMenu.Anchor>
          <Tooltip direction="nw" text={tooltipText}>
            <Button
              variant="invisible"
              size="small"
              className={styles.overflowButton}
              aria-label={accessibleName}
              onClick={event => event.stopPropagation()}
            >
              {`+${hiddenFieldCount}`}
            </Button>
          </Tooltip>
        </ActionMenu.Anchor>
        <ActionMenu.Overlay align="end" onClick={event => event.stopPropagation()}>
          <ActionList className={styles.overflowList} data-testid="issue-field-overflow-menu">
            {fields.map(field => (
              <ActionList.Item
                key={`overflow-field-${field.fieldName}-${field.displayValue}`}
                onSelect={onSelect ? () => onSelect(field.fieldName, field.queryValue) : undefined}
              >
                <span className={styles.fieldItem}>
                  <span className={styles.fieldName}>{field.fieldName}</span>
                  <IssueFieldValueToken displayValue={field.displayValue} />
                </span>
              </ActionList.Item>
            ))}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </ListItemTrailingBadge>
  )
}

/**
 * Shared issue field pill renderer for dashboard list rows. Returns an array of
 * nodes suitable for `ListItemTitle`'s `trailingBadges`, formatting field values
 * through the same `getIssueFieldBadges` helper the Relay row uses so pills
 * render identically. Fields beyond `MAX_INLINE_FIELDS` collapse into a `+N`
 * overflow menu, matching the legacy row.
 */
export function renderIssueFieldBadges({
  fieldValues,
  onSelect,
  filterLinkClassName,
}: RenderIssueFieldBadgesOptions): ReactElement[] {
  const badges = getIssueFieldBadgesFromValues(fieldValues)
  if (badges.length === 0) return []

  const inlineFields = badges.slice(0, MAX_INLINE_FIELDS)
  const hiddenFieldCount = badges.length - inlineFields.length

  const nodes: ReactElement[] = inlineFields.map(badge => (
    <InlineFieldBadge
      key={`field-${badge.fieldName}-${badge.displayValue}`}
      badge={badge}
      onSelect={onSelect}
      filterLinkClassName={filterLinkClassName}
    />
  ))

  if (hiddenFieldCount > 0) {
    nodes.push(
      <IssueFieldOverflowMenu
        key="issue-field-overflow"
        hiddenFieldCount={hiddenFieldCount}
        fields={badges}
        onSelect={onSelect}
      />,
    )
  }

  return nodes
}
